import React from 'react';
import FlowMap, {
  DiffColorsLegend,
  LegendBox,
  LocationTotalsLegend,
} from '@flowmap.gl/react';
import geoViewport from '@mapbox/geo-viewport';

import centroids from './centroids.json';

import './App.css';
import Papa from 'papaparse';

const MAPBOX_TOKEN = process.env.REACT_APP_MapboxAccessToken;

function numeral(x) {
  if (x === '..t') {
    return 0;
  }
  const parsed = parseInt(x && x.replaceAll && x.replaceAll(',', ''));
  if (isNaN(parsed)) {
    return 0;
  }
  return parsed;
}

function getGraph() {
  return new Promise((resolve, reject) => {
    Papa.parse(`${process.env.PUBLIC_URL}/1710004501-eng.csv`, {
      header: true,
      download: true,
      skipEmptyLines: true,

      complete(results, file) {
        resolve(results.data);
      },
      error(err, file) {
        reject(err);
      },
    });
  });
}

const getReferencePeriod = (data) => {
  const referencePeriods = new Set();
  data.forEach((row) => {
    const referencePeriod = row['Reference period'];
    referencePeriods.add(referencePeriod);
  });
  return Array.from(referencePeriods);
};

const getFlowForPeriod = ({ data, period }) => {
  const cleanField = (val) => val?.split(',')[0];

  const flows = [];
  let destination = '';

  data.forEach((row) => {
    const referencePeriod = row['Reference period'];
    destination = cleanField(
      row['Geography, province of destination'] || destination
    );
    if (referencePeriod === period) {
      Object.entries(row)
        .filter(
          ([field]) =>
            field.includes('province') && !field.includes('Geography') && field
        )
        .forEach(([field, value]) => {
          const origin = cleanField(field);
          const id = [origin, destination].join('-');
          flows.push({
            id,
            origin,
            destination,
            value: numeral(value),
            period,
          });
        });
    }
  });
  return flows;
};

const mergewith = (arr1, arr2) => {
  return arr1
    .map((el) => {
      const target = arr2.find((f) => f.id === el.id);
      return { ...el, value: target.value - el.value };
    })
    .filter((result) => result.value);
};

function convertGraph({ data, period, period2 }) {
  const periods = getReferencePeriod(data);
  const flows1 = getFlowForPeriod({ data, period });
  const flows2 = getFlowForPeriod({ data, period: period2 });

  let flows = flows1;
  if (period !== period2) {
    flows = mergewith(flows1, flows2);
  }
  return { flows, periods };
}

const getInitialViewState = () => {
  const bbox = [-133.066406, 41.771312, -53.789063, 65.07213];
  const {
    center: [longitude, latitude],
    zoom,
  } = geoViewport.viewport(
    bbox,
    [window.innerWidth, window.innerHeight],
    undefined,
    undefined,
    512
  );
  return {
    longitude,
    latitude,
    zoom,
    bearing: 0,
    pitch: 0,
  };
};

const colors = {
  flows: {
    scheme: [
      '#f3cbd3',
      '#eaa9bd',
      '#dd88ac',
      '#ca699d',
      '#b14d8e',
      '#91357d',
      '#6c2167',
    ],
  },

  negative: '#d0587e',
  locationAreas: {
    outline: 'rgba(92,112,128,0.5)',
    normal: 'rgba(187,187,187,0.5)',
    selected: 'rgba(217,130,43,0.5)',
  },
};

export default () => {
  const [state, setState] = React.useState({
    locations: null,
    flows: null,
    periods: [],
  });

  const [period, setPeriod] = React.useState('Q1 2019');
  const [period2, setPeriod2] = React.useState('Q1 2020');
  const setData = async () => {
    getGraph().then((result) => {
      fetch(`${process.env.PUBLIC_URL}/canada.json`)
        .then((response) => response.json())
        .then((locations) => {
          const { flows, periods } = convertGraph({
            data: result,
            period,
            period2,
          });
          setState({
            flows: flows.filter((obj) => !obj.id.includes('including')),
            locations,
            periods,
          });
        });
    });
  };

  React.useEffect(() => {
    setData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, period2]);

  const { locations, flows, periods } = state;

  const diffMode = period !== period2;
  if (locations && flows) {
    return (
      <>
        <FlowMap
          pickable={true}
          getLocationId={(loc) => loc?.properties?.NAME}
          getLocationCentroid={(loc) => {
            if (loc && loc.properties) {
              const { lat, lon } = centroids.find(
                (obj) => obj.id === loc?.properties?.NAME
              );
              return [lon, lat];
            } else return [0, 0];
          }}
          getFlowOriginId={(flow) => flow.origin}
          getFlowDestId={(flow) => flow.destination}
          getFlowMagnitude={(flow) => flow.value}
          flows={flows}
          // maxFlowThickness={5}
          locations={locations}
          initialViewState={getInitialViewState()}
          mapboxAccessToken={MAPBOX_TOKEN}
          multiselect={true}
          colors={colors}
          referencePeriod
          diffMode={diffMode}
        />
        <LegendBox bottom={35} left={10} style={{ maxWidth: '30%' }}>
          {diffMode && (
            <>
              <DiffColorsLegend />
              <hr />
            </>
          )}
          <LocationTotalsLegend diff={diffMode} colors={colors} />
        </LegendBox>
        <hr />
        <LegendBox
          bottom={35}
          right={10}
          style={{ maxWidth: '25%', textAlign: 'right' }}
        >
          <label>Compare: </label>
          <select
            name="dates"
            id="date select"
            value={period}
            onChange={(e) => setPeriod(e.currentTarget.value)}
          >
            {periods.map((el) => (
              <option key={el} value={el}>
                {el}
              </option>
            ))}
          </select>
          <label> with </label>
          <select
            name="dates"
            id="date select"
            value={period2}
            onChange={(e) => setPeriod2(e.currentTarget.value)}
          >
            {periods.map((el) => (
              <option key={el} value={el}>
                {el}
              </option>
            ))}
          </select>
          <hr />
          <small>
            <p>
              Data: Statistics Canada{' '}
              <a href="https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=1710004501">
                <span>Table 17-10-0045-01</span>
              </a>
            </p>
            Estimates of interprovincial migrants by province or territory of
            origin and destination, quarterly
            <hr />
            <p>
              Created for graph newsletter ⁂{' '}
              <b>
                <a href="https://cjlm.ca/source/target">source/target</a>{' '}
              </b>{' '}
              using{' '}
              <a href="https://github.com/teralytics/flowmap.gl/">flowmap.gl</a>{' '}
            </p>
            Suggestions or comments?{' '}
            <a href="mailto:%63%68%72%69%73%74%69%61%6E%40%63%6A%6C%6D%2E%63%61">
              Email
            </a>{' '}
            or find me on{' '}
            <a href="https://twitter.com/intent/follow/?screen_name=cjlmiles">
              Twitter
            </a>
          </small>
        </LegendBox>
      </>
    );
  } else return null;
};
